import { HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { BaseService, downloadBlob, IExporterPayloadDTO, ISearchDTO, PageResponse } from '@libs/vc-core-lib';
import { from, Observable, of, switchMap, take, throwError } from 'rxjs';
import { IEventCriteriaDTO } from '../dto/event.criteria.dto';
import { IEventSearchAggregationBySeverityDTO } from '../dto/event.search-aggregation-by-severity.dto';
import { IDashboardModel } from '../model/dashboard.model';
import { IEventModel } from '../model/event.model';
import { ISecureEdgeSearchDTO } from '../model/secure-edge.model';

@Injectable({
    providedIn: 'root',
})
export class EventService extends BaseService<IEventModel> {
    constructor() {
        super(inject(Injector), 'event');
    }

    private static _selectedEventDetail: IEventModel;

    public getSelectedEventDetail(): IEventModel {
        return EventService._selectedEventDetail;
    }

    public setSelectedEventDetail(selectedEventDetail: IEventModel): void {
        EventService._selectedEventDetail = selectedEventDetail;
    }

    public searchEventAggregationBySeverity(
        criteria: IEventCriteriaDTO
    ): Observable<IEventSearchAggregationBySeverityDTO> {
        return this.httpClient.post<IEventSearchAggregationBySeverityDTO>(
            `${this.environment.APP.VC_EVENT_SEARCH_BASE_URL}/${this.serviceBaseUrl}/severity/aggregation`,
            criteria
        );
    }

    public eventSearch(searchDTO: ISearchDTO<IEventCriteriaDTO>): Observable<PageResponse<IEventModel>> {
        return this.httpClient.post<PageResponse<IEventModel>>(
            `${this.environment.APP.VC_EVENT_SEARCH_BASE_URL}/${this.serviceBaseUrl}/pageSearch`,
            searchDTO
        );
    }

    public secureEdgeSearch(secureEdgeDTO: ISecureEdgeSearchDTO): Observable<PageResponse<IEventModel>> {
        return this.httpClient.post<PageResponse<IEventModel>>(
            `${this.environment.APP.VC_EVENT_SEARCH_BASE_URL}/${this.serviceBaseUrl}/pageSearch`,
            secureEdgeDTO
        );
    }

    public getDashboardResult(from: string, to: string, dateInterval: string): Observable<IDashboardModel> {
        return this.httpClient.get<IDashboardModel>(
            `${this.environment.APP.VC_EVENT_SEARCH_BASE_URL}/${this.serviceBaseUrl}/dashboard`,
            { params: new HttpParams().set('from', from).set('to', to).set('dateInterval', dateInterval) }
        );
    }

    public exportEvent(payload: IExporterPayloadDTO<IEventCriteriaDTO>, fileName: string): Observable<void> {
        const url: string = `${this.environment.APP.VC_EVENT_SEARCH_BASE_URL}/${this.serviceBaseUrl}/export`;
        return this.httpClient
            .post(url, payload, {
                observe: 'response',
                responseType: 'blob',
            })
            .pipe(
                take(1),
                switchMap((response: HttpResponse<Blob>) => {
                    const body = response.body;
                    if (!body) {
                        return throwError(() => new Error('Empty export response'));
                    }
                    if (body.type.includes('json')) {
                        return from(body.text()).pipe(switchMap((errorBody) => throwError(() => new Error(errorBody))));
                    }
                    downloadBlob(
                        body,
                        response.headers.get('filename') ?? fileName,
                        response.headers.get('content-type')
                    );
                    return of(undefined);
                })
            );
    }

    public get(id: string): Observable<IEventModel> {
        return this.httpClient.get<IEventModel>(
            `${this.environment.APP.VC_EVENT_SEARCH_BASE_URL}/${this.serviceBaseUrl}/${id}`
        );
    }
}
